import { useState, useEffect, useRef, useCallback } from 'react';

const MOOD_BOOSTER_KEY = 'yelv-mood-booster-enabled';
const FADE_DURATION = 400; // ms

export function useMoodBooster() {
  const [isPlaying, setIsPlaying] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(MOOD_BOOSTER_KEY) === 'true';
    }
    return false;
  });
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio('/audio/mood-booster.mp3');
    audio.loop = true;
    audio.volume = 0;
    audio.preload = 'auto';
    audioRef.current = audio;

    return () => {
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Handle play state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // Start playing with fade-in
      audio.volume = 0;
      audio.play().catch(console.error);
      
      const steps = 20;
      const stepDuration = FADE_DURATION / steps;
      let currentStep = 0;
      
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
      
      fadeIntervalRef.current = setInterval(() => {
        currentStep++;
        audio.volume = Math.min(1, currentStep / steps);
        
        if (currentStep >= steps) {
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
          }
        }
      }, stepDuration);
    } else {
      // Fade out and stop
      const steps = 20;
      const stepDuration = FADE_DURATION / steps;
      let currentStep = steps;
      
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
      }
      
      fadeIntervalRef.current = setInterval(() => {
        currentStep--;
        audio.volume = Math.max(0, currentStep / steps);
        
        if (currentStep <= 0) {
          if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
          }
          audio.pause();
          audio.currentTime = 0;
        }
      }, stepDuration);
    }
  }, [isPlaying]);

  const toggle = useCallback(() => {
    setIsPlaying(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(MOOD_BOOSTER_KEY, String(next));
      }
      return next;
    });
  }, []);

  return { isPlaying, toggle };
}
